import { Form, Input, Modal, Select } from "antd";
import React, { useContext, useEffect, useState } from "react";
import HeaderContext from "../../../components/layout/header/header-context/HeaderContext";
import { putSipNumbersBulkStatusUpdate } from "../../../api/numbers/postSipNumbersBulkStatusUpdate";
import NumericValidator from "../validators/NumericValidator";
import LengthValidator from "../validators/LengthValidator";

interface Props {
  isVisible: boolean;
  handleClose: (refreshData?: boolean) => void;
}

interface State {
  isModalVisible: boolean;
  submitError?: string;
  selectedSipStatus: string;
}

const ManageNumbersFormModal: React.FC<Props> = ({
  isVisible,
  handleClose,
}) => {
  const [form] = Form.useForm();
  const { currentUserId } = useContext(HeaderContext);

  const [state, setState] = useState<State>({
    isModalVisible: false,
    selectedSipStatus: "",
  });

  const { isModalVisible, submitError, selectedSipStatus } = state;

  const handleCancel = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    handleClose();
  };

  const handleSubmit = () => {
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    form
      .validateFields()
      .then((formFields) => {
        console.log("onOk formFields: %o", formFields);
        const payload = {
          sipStatusName: formFields.bulkStatus,
          phoneNumbers: formFields.telephoneNumbers
            .split(",")
            .map((number: string) => number.trim()),
          clientId: currentUserId,
          ...(selectedSipStatus === "Reserved"
            ? { comment: formFields.comment }
            : {}),
        };
        console.log("postSipNumbersBulkStatusUpdate() payload: %o", payload);
        putSipNumbersBulkStatusUpdate(payload).then((postResponse) => {
          console.log("submitResponse:%o", postResponse);
          if (postResponse?.error) {
            setState((prevState) => ({
              ...prevState,
              submitError: postResponse.error,
            }));
          } else {
            form.resetFields();
            setState((prevState) => ({
              ...prevState,
              submitError: "",
            }));
            handleClose();
          }
        });
      })
      .catch((err) => {
        console.log("onOk err: %o", err);
      });
  };

  useEffect(() => {
    if (isVisible !== isModalVisible) {
      setState((prevState) => ({
        ...prevState,
        isModalVisible: isVisible,
        submitError: "",
      }));
    }

    if (!isVisible) return;

    // eslint-disable-next-line
  }, [isVisible, isModalVisible]);

  return (
    <Modal
      title="Manage Numbers"
      maskClosable={false}
      open={isModalVisible}
      onCancel={handleCancel}
      cancelText="Cancel"
      okText="Submit"
      onOk={handleSubmit}
      okButtonProps={{
        className: "bdo-red-button-modal",
      }}
      cancelButtonProps={{
        className: "bdo-grey-button-modal",
      }}
    >
      <div className="my-4" />

      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
        <Form.Item
          name="telephoneNumbers"
          label="Numbers (Comma Separated):"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
            {
              validator: (_, value) =>
                NumericValidator({
                  value: value,
                  form: form,
                  fieldName: "telephoneNumbers",
                }),
            },
            {
              validator: (_, value) => LengthValidator({ value: value }),
            },
          ]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Input.TextArea
            className="h-48"
            placeholder="Enter telephone numbers, comma separated"
            allowClear
          ></Input.TextArea>
        </Form.Item>

        <Form.Item
          name="bulkStatus"
          label="Status:"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Select
            placeholder="Select a status"
            onChange={(selectedLabel) => {
              setState((prevState) => ({
                ...prevState,
                selectedSipStatus: selectedLabel,
              }));
            }}
            allowClear
            options={[
              { title: "", value: "Retired", label: "Retired" },
              { title: "", value: "Reserved", label: "Reserved" },
              { title: "", value: "Unassigned", label: "Unassigned" },
            ]}
          ></Select>
        </Form.Item>

        {selectedSipStatus === "Reserved" && (
          <Form.Item
            name="comment"
            label="Comment:"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Input />
          </Form.Item>
        )}
      </Form>

      {submitError && <div className="text-red-500">{state.submitError}</div>}
    </Modal>
  );
};

export default ManageNumbersFormModal;
